import { useRef, useMemo } from 'react';
import { NODE_WIDTH, NODE_HEIGHT } from './utils/canvasUtils';

/**
 * useContentBounds Hook
 * 
 * Calculates content bounds from node positions for:
 * - Minimap display (with padding)
 * - Pan constraints (raw bounds without padding)
 */
export function useContentBounds({
  tasks,
  nodePositions,
  customPositions,
  nodeDimensions,
  minimapPadding = 800,
}) {
  // Use a ref to store content bounds so callbacks can access latest value
  const contentBoundsRef = useRef({ 
    minX: -minimapPadding, 
    minY: -minimapPadding, 
    maxX: minimapPadding, 
    maxY: minimapPadding, 
    width: minimapPadding * 2, 
    height: minimapPadding * 2 
  });
  
  // Raw content bounds (no padding) for dynamic pan constraints
  const rawBoundsRef = useRef({
    minX: 0, minY: 0, maxX: 0, maxY: 0
  });
  
  // Calculate bounds for the MINIMAP (with padding around content)
  // Pan constraints use rawBoundsRef for dynamic calculations
  const contentBounds = useMemo(() => {
    let bounds;
    
    if (tasks.length === 0) {
      bounds = { 
        minX: -minimapPadding, 
        minY: -minimapPadding, 
        maxX: minimapPadding, 
        maxY: minimapPadding, 
        width: minimapPadding * 2, 
        height: minimapPadding * 2 
      };
      rawBoundsRef.current = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    } else {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      tasks.forEach(task => {
        // Check customPositions, nodePositions, task.position (multi-campaign), and direct task.x/y properties
        const pos = customPositions[task.id] || nodePositions[task.id] || 
          task.position ||
          (task.x !== undefined && task.y !== undefined ? { x: task.x, y: task.y } : null);
        
        // Skip if no position or if position values are invalid (NaN, Infinity, etc.)
        if (!pos || !Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;
        
        const dims = nodeDimensions[task.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
        
        minX = Math.min(minX, pos.x);
        minY = Math.min(minY, pos.y);
        maxX = Math.max(maxX, pos.x + dims.width);
        maxY = Math.max(maxY, pos.y + dims.height);
      });
      
      // Guard against no valid positions found (all tasks skipped)
      if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
        bounds = { 
          minX: -minimapPadding, 
          minY: -minimapPadding, 
          maxX: minimapPadding, 
          maxY: minimapPadding, 
          width: minimapPadding * 2, 
          height: minimapPadding * 2 
        };
        rawBoundsRef.current = { minX: 0, minY: 0, maxX: 0, maxY: 0 };
        contentBoundsRef.current = bounds;
        return bounds;
      }
      
      // Store raw bounds (no padding) for dynamic pan constraints
      rawBoundsRef.current = { minX, minY, maxX, maxY };
        
      // Add padding for minimap display only
      minX -= minimapPadding;
      minY -= minimapPadding;
      maxX += minimapPadding;
      maxY += minimapPadding;
      
      bounds = {
        minX,
        minY,
        maxX,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
      };
    }
    
    contentBoundsRef.current = bounds;
    return bounds;
  }, [tasks, nodePositions, customPositions, nodeDimensions, minimapPadding]);

  return {
    contentBounds,
    contentBoundsRef,
    rawBoundsRef,
  };
}

export default useContentBounds;

